/**
 * Name the gear the character is already wearing, so the build can mark upgrades.
 *
 * Search results carry no slot, so this picker does not know one either; the server resolves
 * each id to its slot and item level when it builds the agent's payload. That also means two
 * items can legitimately map to the same slot, and the highest item level is the one to beat.
 */
import { useState } from 'react';
import { BUILD_SLOTS } from '@thottbot/shared';
import { useSearch } from '../../api/client';

export interface EquippedItem {
  artifactId: string;
  name: string;
}

interface Props {
  value: EquippedItem[];
  onChange: (next: EquippedItem[]) => void;
  disabled?: boolean;
}

const MAX = BUILD_SLOTS.length;

export function EquippedPicker({ value, onChange, disabled }: Props) {
  const [term, setTerm] = useState('');
  const search = useSearch(term);
  const suggestions = (search.data?.groups ?? []).find((g) => g.type === 'item')?.items ?? [];

  function add(name: string): void {
    const match = suggestions.find((s) => s.name.toLowerCase() === name.toLowerCase());
    if (!match || value.length >= MAX || value.some((v) => v.artifactId === match.id)) return;
    onChange([...value, { artifactId: match.id, name: match.name }]);
    setTerm('');
  }

  return (
    <div className="field">
      <label htmlFor="build-equipped">Equipped items</label>
      <input
        id="build-equipped"
        type="text"
        list="build-equipped-options"
        value={term}
        disabled={disabled || value.length >= MAX}
        placeholder="Start typing an item name"
        onChange={(e) => {
          setTerm(e.target.value);
          // A datalist pick fires a change with the full option text, so try to resolve it.
          add(e.target.value);
        }}
      />
      <datalist id="build-equipped-options">
        {suggestions.map((s) => (
          <option key={s.id} value={s.name} />
        ))}
      </datalist>

      {value.length > 0 && (
        <ul className="chips" data-testid="build-equipped">
          {value.map((item) => (
            <li key={item.artifactId} className="chip">
              {item.name}
              {!disabled && (
                <button type="button" aria-label={`Remove ${item.name}`} onClick={() => onChange(value.filter((v) => v.artifactId !== item.artifactId))}>
                  &times;
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {value.length >= MAX && <p className="muted">That is every slot; remove one to add another.</p>}
    </div>
  );
}
